import React, { useState, useEffect, useRef } from 'react';
import { API } from 'aws-amplify';
import qs from 'querystring';
import { makeStyles, useTheme } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Header from './components/Header';
import useSnack from 'hooks/useSnack';
import Form from 'components/form';
import Payment from './components/Payment';
import withConfirm from '../../hooks/withConfirm';
import Input from '../../components/form/Input';
import { validatePhone } from '../../utils';
import PhoneInput from '../../components/form/PhoneInput';
import Select from '../../components/form/Select';
import TIMEZONES from '../../constant/timezones';
import Divider from './components/CustomDivider';

const FormItem = Form.FormItem;

const normalizeEmail = (v, prev) => {
  if (v === prev) {
    return v;
  }
  return v.toLowerCase().trim();
};

const useStyles = makeStyles((theme) => ({
  main: {
    [theme.breakpoints.down('md')]: {},
  },
  sectionTitle: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  payment: {
    '& h3': {
      textAlign: 'left',
    },
  },
}));


const Checkout = ({ product, history, location, confirm, form }) => {
  const classes = useStyles();
  const snack = useSnack();
  const { getFieldDecorator } = form;
  const [checkoutResult, setResult] = useState();
  const [values, setValues] = useState({
    coupon: '',
  });
  const query = qs.parse(location.search.substr(1));
  const theme = useTheme();
  const rootRef = useRef();

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('boot', { app_id: 'xkovkvvi' });
    }

    return () => {
      window.Intercom('shutdown');
    };
  }, []);

  const processPayment = (token) => {
    return form
      .validateFields()
      .then((formData) => {
        return API.post('public', '/users/checkout', {
          body: {
            ...formData,
            planId: query.plan,
            coupon: values.coupon,
            token,
          },
        }).then(
          (resp) => {
            setResult(resp);
          },
          (err) => {
            if (err.response) {
              const { message } = err.response.data;
              if (message === 'Parent account exists') {
                confirm(
                  () => {
                    history.push('/login');
                  },
                  {
                    title: 'Account Exists!',
                    description:
                      "We've found an account with the email address used for parent. If you are already registered with us then please login to purchase.",
                    confirmationText: 'Login',
                  },
                );
              } else {
                snack.error(message);
              }
            } else {
              snack.error(err.message);
            }
          },
        );
      })
      .catch((e) => snack.error('Please check your inputs'));
  };

  if (!query.plan) {
    return <Alert color="error">No product selected.</Alert>;
  }

  const { hideParentDetails, hideStudentDetails } = product.config;

  return (
    <>
      <Header hideNav />
      <main className={classes.main} ref={rootRef}>
        <Grid container>
          <Grid item xs={12} md={4}>
            <Box
              marginTop={{ xs: 2, md: 0 }}
              marginBottom={{ xs: 3, md: 0 }}
              maxWidth={300}
            >
              <Typography gutterBottom variant="h3">
                Congratulations, you’re in
                <span style={{ color: theme.palette.primary.main }}>.</span>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            {checkoutResult ? (
              <Box>
                <Divider />
                <Typography gutterBottom variant="h3">
                  Welcome to WeAdmit
                </Typography>
                <Typography variant="body2">
                  Thank you for choosing WeAdmit. We will follow-up with an email containing all of the
                  necessary onboarding info. Your college admissions journey has
                  just begun!
                </Typography>
              </Box>
            ) : (
              <div>
                <Box mb={5}>
                  <Divider />
                  <Typography>
                    After carefully reviewing your application, we’re delighted to
                    welcome you to the upcoming cohort! As you know, this is a
                    select group and we’re only inviting a limited number of
                    participants. Please submit payment at your earliest convenience
                    so that your seat will be secured. Once your payment is
                    received, we will follow-up with an email containing all of the
                    necessary onboarding info. Your college admissions journey has
                    just begun!
                  </Typography>
                </Box>
                <Typography variant="h3" className={classes.sectionTitle}>
                  Fill in information
                </Typography>
                <Box marginBottom={3}>
                  <Typography color="primary" component="span">
                    ☑
                  </Typography>{' '}
                  All fields are required
                </Box>

                <Form className={classes.root}>
                  {!hideParentDetails && (
                    <Grid container>
                      <Grid item md={12}>
                        <Typography
                          variant="h5"
                          className={classes.sectionTitle}
                        >
                          Add Parent Info
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.firstName', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your first name',
                              },
                            ],
                          })(<Input label="First Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.lastName', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your last name',
                              },
                            ],
                          })(<Input label="Last Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.email', {
                            normalize: normalizeEmail,
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your email address',
                              },
                              {
                                type: 'email',
                                message: 'Please enter a valid email',
                              },
                            ],
                          })(
                            <Input
                              label="Parent/Guardian Email"
                              type="email"
                              hint="We'll create a Guardian account for you and send updates"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.emailConfirm', {
                            normalize: normalizeEmail,
                            rules: [
                              {
                                required: true,
                                message: 'Please confirm your email address',
                              },
                              {
                                type: 'email',
                                message: 'Please enter a valid email',
                              },
                            ],
                          })(
                            <Input
                              label="Confirm Email"
                              type="email"
                              hint="Please enter your email address again"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.phone', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your phone number',
                              },
                              {
                                validator: validatePhone,
                                skipNetwork: true,
                              },
                            ],
                          })(
                            <PhoneInput
                              label="Parent/Guardian Mobile Number"
                              hint="We'll send text updates to your mobile number"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.address1', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your address',
                              },
                            ],
                          })(
                            <Input label="Address" hint="Your home address" />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('parent.city', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter city',
                              },
                            ],
                          })(<Input label="City" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={6}>
                        <FormItem>
                          {getFieldDecorator('parent.state', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter state',
                              },
                            ],
                          })(<Input label="State" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={6}>
                        <FormItem>
                          {getFieldDecorator('parent.zip', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter zip code',
                              },
                            ],
                          })(<Input label="Zip code" type="number" />)}
                        </FormItem>
                      </Grid>
                    </Grid>
                  )}
                  {!hideStudentDetails && (
                    <Box>
                      <Typography variant="h5" className={classes.sectionTitle}>
                        Student Details
                      </Typography>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('student.firstName', {
                              rules: [
                                {
                                  required: true,
                                  message: 'Please enter your first name',
                                },
                              ],
                            })(<Input label="Student First Name" />)}
                          </FormItem>
                        </Grid>
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('student.lastName', {
                              rules: [
                                {
                                  required: true,
                                  message: 'Please enter your last name',
                                },
                              ],
                            })(<Input label="Student Last Name" />)}
                          </FormItem>
                        </Grid>
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('student.email', {
                              normalize: normalizeEmail,
                              rules: [
                                {
                                  required: true,
                                  message: 'Please enter your email address',
                                },
                                {
                                  type: 'email',
                                  message: 'Please enter a valid email',
                                },
                              ],
                            })(
                              <Input
                                type="email"
                                label="Student Email"
                                hint="We'll create a Student account and send updates"
                              />,
                            )}
                          </FormItem>
                        </Grid>
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('student.emailConfirm', {
                              normalize: normalizeEmail,
                              rules: [
                                {
                                  required: true,
                                  message: 'Please confirm your email address',
                                },
                                {
                                  type: 'email',
                                  message: 'Please enter a valid email',
                                },
                              ],
                            })(
                              <Input
                                type="email"
                                label="Confirm Email"
                                hint="Please enter student's email address again"
                              />,
                            )}
                          </FormItem>
                        </Grid>
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('student.phone', {
                              rules: [
                                {
                                  required: true,
                                  message: 'Please enter your phone number',
                                },
                                {
                                  validator: validatePhone,
                                  skipNetwork: true,
                                },
                              ],
                            })(
                              <PhoneInput
                                label="Student Mobile Number"
                                hint="We will send text updates to student’s mobile number"
                              />,
                            )}
                          </FormItem>
                        </Grid>
                        {!hideParentDetails && (
                          <Grid item xs={12}>
                            <FormItem>
                              {getFieldDecorator('parent.relationship', {
                                rules: [
                                  {
                                    required: true,
                                    message: 'Please enter relationship',
                                  },
                                ],
                              })(
                                <Input label="What is your relationship with the student?" />,
                              )}
                            </FormItem>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <FormItem>
                            {getFieldDecorator('timezone', {
                              rules: [
                                {
                                  required: true,
                                  message: 'Please select timezone',
                                },
                              ],
                            })(
                              <Select
                                noMargin
                                transformValue
                                isMulti={false}
                                label="Timezone?"
                                options={TIMEZONES}
                              />,
                            )}
                          </FormItem>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Form>
                <Box mt={4} className={classes.payment}>
                  <Divider />
                  <Payment
                    showAgreement
                    data={values}
                    onChange={setValues}
                    product={product}
                    onSubmit={processPayment}
                  />
                </Box>
              </div>
            )}
          </Grid>
        </Grid>
      </main>
    </>
  );
};

export default withConfirm(Form.create()(Checkout));
